import type { ReactNode } from "react";
import { COLOR_VEREDICTO } from "@/components/buzon/FilaCorreoBuzon";
import type { TipoVeredicto } from "@/lib/api";
import type { ContenidoAyuda, ItemAyuda } from "@/ui/AyudaVista";
import { ATAJOS_VISOR, BOTONES_VISOR } from "./visorArchivo";

/**
 * Ayuda de «Revisar mi correo» (`components/buzon/RevisarCorreoModal`).
 * Reemplaza al «?» con globito que tenía, más lo que iba en los tooltips de
 * sus botones, sus pastillas y sus flechas.
 *
 * Lo que dice sobre el veredicto sale de `domain/buzon_reglas.py`
 * (`clasificar`) y lo de la IA, de `services/buzon/lectura.py`
 * (`aplicar_ia`): si cambian las reglas, cambia esto.
 */

function Pastilla({ tipo, children }: { tipo: TipoVeredicto; children: ReactNode }) {
  return (
    <span
      className={`inline-flex rounded border px-1.5 py-0.5 text-[10px] uppercase tracking-wide ${COLOR_VEREDICTO[tipo]}`}
    >
      {children}
    </span>
  );
}

export function ayudaRevisarCorreo({
  enIgnorados,
  apagado,
}: {
  /** Filtro «Ignorados»: el pie solo ofrece «Devolver a la lista». */
  enIgnorados: boolean;
  /** El usuario no ha conectado su correo: la ventana solo invita a hacerlo. */
  apagado: boolean;
}): ContenidoAyuda {
  const intro = (
    <>
      <p>
        Tu buzón de correo, en solo lectura, dentro de ArkTickets. <b>No es tu bandeja completa</b>:
        abre en «Puede servir», lo que ArkTickets cree que te falta capturar.
      </p>
      <p>
        <b>Mirar no guarda nada</b> ni toca tu correo: no marca como leído, no mueve y no borra.
        Solo se guarda algo cuando traes un correo a ArkTickets o lo ignoras.
      </p>
    </>
  );

  if (apagado) {
    return {
      intro,
      secciones: [
        {
          titulo: "Qué hace cada botón",
          items: [
            {
              label: "Conectarlo en mi Perfil",
              text: "Te lleva a tu Perfil para conectar tu correo empresarial. Hasta entonces esta ventana no tiene nada que enseñarte.",
            },
          ],
        },
      ],
    };
  }

  const pie: ItemAyuda[] = enIgnorados
    ? [
        {
          label: "Devolver a la lista",
          text: "Deshace el «Ignorar»: el correo vuelve a ofrecerse en su filtro.",
        },
      ]
    : [
        {
          label: "Ignorar",
          text: "No borra nada ni toca tu correo: solo deja de ofrecértelo. Se deshace desde el filtro «Ignorados».",
        },
        {
          label: "Abrir caso (N)",
          text: "Trae los correos a ArkTickets y abre el formulario de caso nuevo ya prellenado; al crear el caso, los correos quedan en él con sus archivos como evidencias (también los que vienen como enlace). Si cancelas, se quedan en la Bandeja sin caso. Si para entonces un correo ya no está en tu buzón, entra al caso sin sus archivos y te avisa.",
        },
        {
          label: "Agregar al CASO-… / Agregar a un caso…",
          text: "«Agregar al …» sale cuando los correos ya apuntan a un caso: los mete a su bitácora, con sus archivos como evidencias. «Agregar a un caso…» abre el buscador de casos para elegir a cuál. Si un correo ya vive en otro caso, no lo mueve: te avisa.",
        },
        {
          label: "Otro caso…",
          text: "Sale en lugar de «Abrir caso» cuando los correos ya apuntan a un caso: te deja elegir otro a mano.",
        },
        {
          label: "Incluir casos cerrados",
          text: "En el buscador de casos. Apagado, solo busca casos abiertos: pegarle un correo a un caso cerrado revive una conversación que alguien dio por terminada.",
        },
      ];

  return {
    intro,
    secciones: [
      {
        titulo: "Qué hace cada botón",
        items: [
          {
            label: "Puede servir · Todo el buzón · Ignorados",
            text: "Filtros. «Puede servir»: lo que parece de un caso, de un proveedor o de un cliente, lo que suena a trabajo, lo que rescató la IA y lo que está en tu Bandeja sin caso. «Todo el buzón»: todo lo de los días que se muestran. «Ignorados»: lo que ignoraste. El número es cuántos hay. Cambiar de filtro limpia la selección.",
          },
          {
            label: "Ver todo el buzón · Buscar más atrás",
            text: "Salen cuando la lista está vacía. «Buscar más atrás» amplía la búsqueda a 90 días y después a 365.",
          },
          {
            label: "Switch de cada correo",
            text: "Lo selecciona para actuar sobre varios a la vez. Los que ya están en un caso no se pueden seleccionar.",
          },
          { label: "Un correo de la lista", text: "Tócalo para leerlo completo." },
          {
            label: "‹ ›",
            text: "Correo anterior y siguiente, dentro de la lista que estás viendo. En el celular también puedes deslizar de lado.",
          },
          { label: "‹ Volver a la lista", text: "En el celular, regresa del correo a la lista." },
          {
            label: "Un archivo del correo",
            text: "Lo abre en el visor, sin salir de la ventana.",
          },
          {
            label: "TEXTO / HTML",
            text: "Cambia entre el texto plano del correo y el correo con su formato original.",
          },
          ...pie,
          { label: "Cerrar", text: "Cierra la ventana (en el celular, con la X de arriba)." },
        ],
        cuerpo: (
          <p>
            Los botones de abajo actúan sobre los correos seleccionados; si no hay ninguno, sobre
            el correo que estás viendo. Salen grises sin ninguno de los dos.
          </p>
        ),
      },
      {
        titulo: "Qué significa cada pastilla",
        items: [
          {
            label: <Pastilla tipo="ancla">CASO-…</Pastilla>,
            text: "Es de un caso: responde a un correo que mandamos desde ese caso, o su asunto trae el TE de un caso o el IMEI de un solo caso abierto. Dice «(cerrado)» si ese caso ya se cerró.",
          },
          {
            label: <Pastilla tipo="proveedor">Parece del proveedor</Pastilla>,
            text: "El remitente es un contacto de proveedor, o escribe desde su dominio.",
          },
          {
            label: <Pastilla tipo="cliente">Parece de un cliente</Pastilla>,
            text: "El remitente es un contacto de cliente, o escribe desde su dominio.",
          },
          {
            label: <Pastilla tipo="clave">Suena a trabajo</Pastilla>,
            text: "No conocemos al remitente, pero el asunto trae una palabra de soporte. Así se cuela el cliente nuevo que escribe por primera vez.",
          },
          {
            label: <Pastilla tipo="ia">✨ Podría servir</Pastilla>,
            text: "Las reglas no lo supieron clasificar y la IA del buzón cree que sí sirve.",
          },
          {
            label: <Pastilla tipo="sin_caso">En tu bandeja, sin caso</Pastilla>,
            text: "Ya entró a ArkTickets, pero todavía no tiene caso.",
          },
          {
            label: <Pastilla tipo="ya_esta">Ya está en …</Pastilla>,
            text: "Ya vive en ese caso (o ya lo trajiste). No se puede seleccionar.",
          },
          {
            label: (
              <span className="flex flex-wrap gap-1">
                <Pastilla tipo="descartado">Lo descartaste</Pastilla>
                <Pastilla tipo="ignorado">Lo ignoraste</Pastilla>
              </span>
            ),
            text: "Ya decidiste sobre él (en la Bandeja o aquí). Descartar o ignorar no es borrar: se puede deshacer.",
          },
          {
            label: (
              <span className="flex flex-wrap gap-1">
                <Pastilla tipo="ruido">Automático</Pastilla>
                <Pastilla tipo="indeciso">Sin clasificar</Pastilla>
              </span>
            ),
            text: "Viene de una dirección automática (no espera respuesta de nadie), o ninguna regla lo reconoció.",
          },
          {
            label: "Por qué: …",
            text: "El renglón chiquito bajo las pastillas: de dónde salió el veredicto («Viene de un contacto de cliente», «El asunto trae el folio …»).",
          },
          {
            label: "Entra solo",
            text: "Pertenece sin duda a un caso abierto y el radar del buzón lo va a meter solo a la bitácora de su caso en su próxima vuelta (días hábiles, en su horario). Solo sale con el radar prendido (Configuración → Correo y empresa → Buzón) y en correos que llegaron después de su última vuelta: los que el radar ya vio no los vuelve a mirar. Sin la pastilla, agrégalo tú.",
          },
          {
            label: "Clip con número",
            text: "Cuántos archivos trae el correo. «(sin texto, solo archivos)»: el correo no trae texto.",
          },
          {
            label: "enlace (en un archivo)",
            text: "El archivo no viene dentro del correo: vive en el servidor de quien te escribió y ArkTickets lo trae por ti. Se guarda como evidencia cuando agregas el correo a un caso.",
          },
          { label: "⚠ Cerrado", text: "En el buscador de casos: ese caso ya se cerró." },
        ],
      },
      { titulo: "Al ver un archivo", items: BOTONES_VISOR },
      {
        titulo: "Atajos de teclado",
        items: [
          { label: "← / →", text: "Con un correo abierto: el correo anterior o el siguiente de la lista. Con el visor abierto, mueven el visor, no el correo." },
          ...ATAJOS_VISOR,
        ],
      },
      {
        titulo: "Qué se le manda a la IA",
        cuerpo: (
          <p>
            Si la IA del buzón está prendida (Configuración → Correo y empresa → Buzón), de los
            correos que las reglas no logran clasificar viajan <b>solo el título y el remitente</b>:
            nunca el contenido ni los archivos. Los juzga en segundo plano para no hacerte
            esperar, así que su «✨ Podría servir» puede tardar en salir hasta que vuelvas a abrir
            esta ventana.
          </p>
        ),
      },
    ],
  };
}
